import React, { PureComponent } from 'react';
import { muiTheme } from 'app/themes/materialUi';
import PropTypes from 'prop-types';
import produce from 'immer';
import { Tooltip, Button, Divider, TextField } from '@mic3/platform-ui';
import styled from 'styled-components';

import VirtualListManaged from 'app/components/molecules/VirtualList/VirtualListManaged';
import AddResourcesModal from 'app/containers/Common/AddResourcesModal';
import Icon from 'app/components/atoms/Icon/Icon';

import { bind, debounce, memoize } from 'app/utils/decorators/decoratorUtils';
import Immutable from 'app/utils/immutable/Immutable';
import { isEmpty } from 'app/utils/utils';
import { get } from 'app/utils/lo/lo';
import { typeMap } from 'app/config/typesConfig';
import { canBeuuid, isValiduuid } from 'app/utils/string/string-utils';


import { CardDivider } from 'app/containers/Entities/EntitiesList/EntitiesListItem';
import TypeIcon from 'app/components/atoms/TypeIcon/TypeIcon';
import ResizableListItem from 'app/components/molecules/VirtualList/ResizableListItem';

import {
    StyledExpansionCard,
    ListItemStyled,
    StyledAvatar,
    StyledAvatarOverlay,
    ButtonWrapper,
    ListStyled,
    ExpansionListItem,
    ExpansionSummary,
    getSubTitle as getSubTitleUtil,
    getAvatarUrl,
    computeHasLowerAccess,
    renderListItemActions as renderListItemActionsUtil,
} from './selectionListUtils';
import TeamWorkspacesList from './TeamWorkspacesList';


const SearchField = styled(TextField)`
    padding: 8px 16px !important;
`;

const SearchWrapper = styled.div`
    margin: 0 !important;
    position: sticky !important;
    top: 0;
    background: ${({ theme }) => theme.material.colors.background.default} !important;
    z-index: 1000;
    padding-bottom: 16px;
`;

const StyledDivider = styled(Divider)`
    margin-bottom: 15px !important;
`;

const ListContainer = styled.div`
    position: relative;
    overflow-x: hidden;
    height: 100%;
`;

/**
 * Renders the view to display the classification.
 */
class LazySelectionList extends PureComponent {
    static propTypes = {
        type: PropTypes.string.isRequired,
        typeChild: PropTypes.string,
        isLoading: PropTypes.bool,
        disabled: PropTypes.bool,
        disabledAdd: PropTypes.bool,
        list: PropTypes.array.isRequired,
        filteredList: PropTypes.array,
        onAdd: PropTypes.func.isRequired,
        onRemove: PropTypes.func.isRequired,
        listIcon: PropTypes.string,
        expansionDetailCaption: PropTypes.string,
        lazyList: PropTypes.bool,
        totalRecords: PropTypes.number,
        startIndex: PropTypes.number,
        loadData: PropTypes.func,
    };

    static defaultProps = {
        disabled: false,
        showRoles: false,
    };

    state = Immutable({
        selectedListIds: [],
        isAddOpen: false,
        searchValue: '',
        expandedItems: {},
        searchFilters: [],
    });

    defaultState = this.state;

    listRef = React.createRef();

    componentDidUpdate(_, prevState) {
        const searchValue = this.state.searchValue?.trim();
        const prevSearchValue = prevState.searchValue?.trim();
        if (prevSearchValue !== searchValue) {
            this.debouncedSearchHandler(searchValue);
        }
    }


    @bind
    refreshList() {
        const ref = this.listRef.current;
        if (!ref) return;
        if (typeof ref.forceUpdate === 'function') {
            ref.forceUpdate();
        }
    }

    @bind
    updateItemData(itemId, updatedData) {
        const ref = this.listRef.current;
        if (!ref) return;
        if (typeof ref?.updateItemData === 'function') {
            ref.updateItemData(itemId, updatedData);
        }
    }

    @bind
    setDefaultState() {
        this.setState(this.defaultState, this.refreshList);
    }

    @bind
    openAddModal(evnt) {
        evnt.stopPropagation();
        this.setState({ isAddOpen: true });
    }

    @bind
    closeAddModal() {
        this.setState({ isAddOpen: false });
    }

    @bind
    removeTeam(evnt) {
        evnt.stopPropagation();
        const { selectedListIds } = this.state;
        if (isEmpty(selectedListIds)) {
            return;
        }
        return this.props.onRemove(selectedListIds).then(this.setDefaultState);
    }

    @bind
    removeOne(event, userId) {
        event.preventDefault();
        event.stopPropagation();
        if (!userId) {
            return;
        }
        return this.props.onRemove([userId]).then(() => {
            const selected = this.getSelected(userId);
            if (selected) {
                this.setState(
                    prevState => ({
                        selectedListIds: prevState.selectedListIds.filter(id => id !== userId),
                    }),
                    this.refreshList
                );
            }
        });
    }

    @bind
    @memoize()
    getSubTitle(data) {
        const { type } = this.props;
        return getSubTitleUtil(type, data);
    }

    @bind
    getSelected(userId) {
        if (!userId) {
            return false;
        }
        const { selectedListIds } = this.state;
        return selectedListIds.includes(userId);
    }

    @bind
    onSelect(event, userId) {
        
        event.preventDefault();
        if (!userId) {
            return;
        }
        const selected = this.getSelected(userId);
        if (!selected) {
            this.setState(
                produce(draft => {
                    draft.selectedListIds.push(userId);
                }),
                this.refreshList
            );
        } else {
            this.setState(
                prevState => ({
                    selectedListIds: prevState.selectedListIds.filter(id => id !== userId),
                }),
                this.refreshList
            );
        }
    }

    @bind
    onSubmit(list) {
        const { valueField } = this.props;
        if (isEmpty(list)) {
            return;
        }
        return this.props.onAdd(valueField ? list.map(item => item[valueField]) : list).then(this.setDefaultState);
    }

    @bind
    onSearch(event) {
        const searchValue = event.target.value;
        this.setState({ searchValue });
    }

    @bind
    @debounce()
    debouncedSearchHandler(searchValue) {
        const filters = [];

        if (searchValue) {
            let filter = { field: 'name', op: 'contains', value: searchValue };
            if(isValiduuid(searchValue)) {
                filter = { field: 'id', op: '=', value: searchValue };
            } else if (canBeuuid(searchValue)) {
                filter = { field: 'id', op: 'startsWith', value: searchValue, cast: 'text' };
            }
            filters.push(filter);
        }

        // Update searchFilters in state - this will trigger VirtualListManaged to reset
        this.setState({ searchFilters: filters });
    }

    @bind
    @memoize()
    renderListItemActions(data, hasLowerAccess) {
        return renderListItemActionsUtil(this.props, data, hasLowerAccess, this.removeOne);
    }

    @bind
    handleExpandClick(event, id) {
        event.stopPropagation();
        this.setState((prevState) => ({
            expandedItems: {
                ...prevState.expandedItems,
                [id]: !prevState.expandedItems?.[id],
            }
        }), this.refreshList);
        
    }

    @bind
    renderComponent({ style, index, data, resize, selected }) {
        const { disabled: isListDisabled, type, expansionDetailCaption, noSubTitle, userRole, showChildRoles, typeChild } = this.props;
        const { id, name, image } = data;
        let avatar = null;
        let hasLowerAccess = false;
        const disabled = !!data?.disabled || isListDisabled;
        hasLowerAccess = computeHasLowerAccess(userRole, data?.role);
        avatar = getAvatarUrl(type, id, image);

        if (type === 'team') {
            const handleResize = resizeRow => {
                setTimeout(() => {
                    resizeRow();
                }, 200);
            };

            return (
                <ResizableListItem style={style} key={id} index={index} resize={resize} padding={3}>
                    {resizeRow => (
                        <ExpansionListItem
                            expanded={!!this.state.expandedItems?.[id]}
                            TransitionProps={{ timeout: 0 }}
                            onChange={() => handleResize(resizeRow)}
                            selected={selected}
                            square
                        >
                            <ExpansionSummary expandIcon={<Icon name="chevron-down" />} IconButtonProps={{ edge: false, onClick: (e) => this.handleExpandClick(e, id) }}>
                                <ListItemStyled
                                    disabled={disabled}
                                    component={
                                        <div>
                                            <StyledAvatar src={avatar} initials={name} size="lg" />
                                            <StyledAvatarOverlay selected={selected} />
                                        </div>
                                    }
                                    title={
                                        <>
                                            <TypeIcon type={type} size="sm" />
                                            <CardDivider flexItem orientation="vertical" />
                                            {get(data, 'name')}
                                        </>
                                    }
                                    subTitle={noSubTitle ? '' : this.getSubTitle(data)}
                                    onClick={e => {
                                        
                                        e.stopPropagation();
                                        (!disabled && !hasLowerAccess) && this.onSelect(e, get(data, 'id'));
                                    }}
                                    actions={this.renderListItemActions(data, hasLowerAccess)}
                                    raised
                                />
                            </ExpansionSummary>
                            {this.state.expandedItems?.[id] && <TeamWorkspacesList
                                teamId={id}
                                expansionDetailCaption={expansionDetailCaption}
                                noSubTitle={noSubTitle}
                                showChildRoles={showChildRoles}
                                typeChild={typeChild}
                                refreshList={this.refreshList}
                            /> }
                        </ExpansionListItem>
                    )}
                </ResizableListItem>
            );
        }

        return (
            <div style={style} key={id}>
                <ListItemStyled
                    disabled={disabled}
                    component={
                        <div>
                            <StyledAvatar selected={selected} src={avatar} initials={name} size="lg" />
                            <StyledAvatarOverlay selected={selected} />
                        </div>
                    }
                    title={
                        <>
                            <TypeIcon type={type} size="sm" />
                            <CardDivider flexItem orientation="vertical" />
                            {get(data, 'name')}
                        </>
                    }
                    subTitle={noSubTitle ? '' : this.getSubTitle(data)}
                    onClick={e => (!disabled && !hasLowerAccess) && this.onSelect(e, get(data, 'id'))}
                    selected={selected}
                    actions={this.renderListItemActions(data, hasLowerAccess)}
                    raised
                />
            </div>
        );
    }

    @bind
    buildLazyList(list, listKey, totalRecords){
        const { type, isLoading, loadData } = this.props;
        const { searchFilters } = this.state;
        return (
            <VirtualListManaged
                key={listKey}
                ref={this.listRef}
                itemSize={85}
                itemCount={totalRecords}
                list={list}
                startIndex={this.props.startIndex}
                isLoading={!!isLoading}
                loadData={loadData}
                filterBy={searchFilters}
                renderComponent={({ index, style, resize, data }) => {
                    const selected = this.getSelected(data?.id);
                    return data ? this.renderComponent({ style, index, resize, data, selected }) : (
                        <div style={style} key={index} />
                    );
                }}
                maxWidth="1024"
                title={`${totalRecords >= 1000 ? '999+' : totalRecords} ${typeMap[type]}`}
            />
        )
    }
    @bind
    buildList(list, selectedListIds, listKey, totalRecords) {
        const { buildList, maxHeight, fullHeight } = this.props;
        if (buildList) {
            return buildList(list, selectedListIds);
        }
      
        return (
            <ListStyled items={totalRecords} itemSize={121} maxHeight={maxHeight || 500} fullHeight={fullHeight}>
                { this.buildLazyList(list, listKey, totalRecords) }
            </ListStyled>
        );
    }

    render() {
        const { list, type, title, expanded, disabled, disabledAdd, totalRecords, showSearch } = this.props;
        const { selectedListIds, listKey, searchValue } = this.state;
        return (
            <ListContainer>
                <StyledExpansionCard expanded={!!expanded} title={title}>
                    {showSearch && (
                        <SearchWrapper>
                            <SearchField
                                fullWidth
                                variant="standard"
                                margin="none"
                                placeholder="Search..."
                                InputProps={{ disableUnderline: true }}
                                value={searchValue}
                                onChange={this.onSearch}
                            />
                            <StyledDivider />
                        </SearchWrapper>
                    )}
                    {isEmpty(this.state.selectedListIds)
                        ? !disabledAdd &&
                          !disabled && (
                            <Tooltip arrow title="Click to add">
                                <ButtonWrapper className="button-wrapper">
                                    <Button onClick={this.props.onAddClick || this.openAddModal}>
                                        <Icon type="mdi" name="plus" size="md" hexColor={muiTheme.colors.text.button} /> ADD NEW
                                    </Button>
                                </ButtonWrapper>
                            </Tooltip>
                        )
                        : !disabled && (
                            <Tooltip arrow title={`Click to remove selected list`}>
                                <ButtonWrapper className="button-wrapper">
                                    <Button variant="text" onClick={this.removeTeam}>
                                        <Icon hexColor={muiTheme.colors.secondary.main} type="mdi" name="delete" size="md" /> REMOVE
                                    </Button>
                                </ButtonWrapper>
                            </Tooltip>
                        )}
                    {this.buildList(list, selectedListIds, listKey, totalRecords)}
                </StyledExpansionCard>
                <AddResourcesModal
                    type={type}
                    isBulkSelect={true}
                    isOpen={this.state.isAddOpen}
                    onSubmit={this.onSubmit}
                    closeModal={this.closeAddModal}
                    applicableOn={this.props.applicableOn}
                    filterBy={this.props.filterBy}
                    excludeBy={this.props.excludeBy}
                    showRoles={this.props.showRoles}
                    userRole={this.props.userRole}
                    withoutClose
                />
            </ListContainer>
        );
    }
}

export default LazySelectionList;
